import { EWorkspaceProjectRole, IProjectUserRole, IWorkspaceProjectRole } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { ProjectService } from "../../../../services/project-service";
import { UserService } from "../../../../services/user-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import "./transfer-project-ownership-modal.scss";

interface ITransferOwnershipFormValues {
  userName: string;
  oldOwnerNewProjectRole: number;
}

export interface IState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isLoading?: boolean;
  projectRoleList: IWorkspaceProjectRole[];
}

/** What the commit is doing, rather than the generic "Saving". */
const TRANSFER_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Transferring", success: "Transferred" };

export interface IProps {
  projectId: string;
  isModalOpen: boolean;
  closeModal: (isReloadRequired: boolean) => void;
  sharedUsersForTransfer: IProjectUserRole[];
  projectName: string;
  loadProjectList: () => void;
}

const TRANSFER_OWNERSHIP_FORM_NAME: string = "transferOwnershipForm";

class TransferProjectOwnershipModal extends React.PureComponent<IProps & IWithHistoryProp & IWithContextProp, IState> {
  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IProps & IWithHistoryProp & IWithContextProp) {
    super(props);

    this.state = {
      saveStatus: "idle",
      isLoading: false,
      projectRoleList: []
    };
  }

  async componentDidMount() {
    await this.loadProjectRoleList();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  async loadProjectRoleList() {
    try {
      const response = await ProjectService.GetProjectRoleList();
      const roles: IWorkspaceProjectRole[] = response.data?.filter(
        (role) => role.enumId !== EWorkspaceProjectRole.ProjectOwner
      );
      this.setState({
        projectRoleList: roles
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  // Build the selectable owner candidates: exclude the current owner by role enum (not the brittle
  // localized role string) and collapse duplicate rows so a user who holds more than one project role
  // is offered only once. This removes the "name duplication" and the React duplicate-key warning.
  private getTransferCandidates = (): IProjectUserRole[] => {
    const byUserId: Map<string, IProjectUserRole> = new Map<string, IProjectUserRole>();
    (this.props.sharedUsersForTransfer ?? [])
      .filter((user: IProjectUserRole) => user.enumId !== EWorkspaceProjectRole.ProjectOwner)
      .forEach((user: IProjectUserRole) => {
        if (!byUserId.has(user.userId)) {
          byUserId.set(user.userId, user);
        }
      });
    return Array.from(byUserId.values());
  };

  saveUser = async (values: ITransferOwnershipFormValues) => {
    // A second submit while the commit runs or its ✓ is held (the button is live again during the
    // hold, and Enter submits the form regardless) would call the service twice and cancel the close.
    if (this.saveStatus.current === "loading" || this.saveStatus.current === "success") return;
    const userToTransfer = this.getTransferCandidates().find(
      (user: IProjectUserRole) => user.userName === values.userName
    );
    const newOwnerId = userToTransfer?.userId;
    const oldOwnerId = this.props.context.userInfo.id;
    if (!newOwnerId || !oldOwnerId) {
      return;
    }
    this.saveStatus.start();
    try {
      const res = await UserService.ProjectOwnershipTransfer(
        oldOwnerId,
        newOwnerId,
        this.props.projectId,
        values.oldOwnerNewProjectRole
      );
      if (res && res.status === 200) {
        // Handing a project over leaves nothing on this screen to point at, so the toast stays; the
        // dialog waited for its own ✓ before leaving — see `SaveStatus`.
        KMainFunctions.displaySuccessNotification("Project ownership transfered successfully.");
        this.saveStatus.succeed((): void => this.props.closeModal(true));
        return;
      }
      this.saveStatus.fail();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.saveStatus.fail();
    }
  };

  saveUserFailed = (): void => {};

  handleClickCancel = () => {
    // Closing during the ✓ hold would cancel the close the success already scheduled (and with it
    // the reload/refresh that follows), so it is swallowed; the dialog leaves on its own a moment later.
    if (this.saveStatus.current === "success") return;
    // The dialog stays mounted between openings, so a commit abandoned mid-flight must not leave
    // the submit guard latched for the next one.
    this.saveStatus.reset();
    this.props.closeModal(false);
  };

  // Options for the "Search member" selectbox are derived from the deduped, owner-excluded
  // candidate list so the picker never shows the current owner or duplicate names.
  private getMemberOptions = (): KuikaSelectboxOption[] => {
    return this.getTransferCandidates().map((user: IProjectUserRole) => ({
      label: user.userName,
      value: user.userName,
      testId: `transferOwnershipMemberOption-${user.userName}`
    }));
  };

  private getRoleOptions = (): KuikaSelectboxOption[] => {
    return (
      this.state.projectRoleList?.map((role) => ({
        label: role.name,
        value: role.enumId,
        testId: `transferOwnershipRoleOption-${role.enumId}`
      })) ?? []
    );
  };

  render() {
    const title: string = "Change project owner";
    const footer = (
      <KModalFooter
        submitLabel="Confirm ownership transfer"
        submitTestId="transferOwnershipConfirmButton"
        submitForm={TRANSFER_OWNERSHIP_FORM_NAME}
        status={this.state.saveStatus}
        statusLabels={TRANSFER_STATUS_LABELS}
      />
    );

    return (
      <KModal
        showModal={this.props.isModalOpen}
        closeModal={this.handleClickCancel}
        header={title}
        footer={footer}
        width={400}
        isLoading={this.state.isLoading}
      >
        <div className="kuika-transfer-ownership kuika-flex-column--gap-l kuika-padding-l">
          <div className="kuika-transfer-ownership__header">
            <KuikaModalIconWrapper iconName="users-group" intent="neutral" />
            <KuikaH5>{title}</KuikaH5>
          </div>
          <KuikaText className="kuika-transfer-ownership__description">
            Select the user who will own the project of{" "}
            <KuikaTextBold className="kuika-transfer-ownership__project-name">{this.props.projectName}</KuikaTextBold>
          </KuikaText>
          <Form
            name={TRANSFER_OWNERSHIP_FORM_NAME}
            className="kuika-transfer-ownership__form kuika-flex-column--gap-l"
            onFinish={this.saveUser}
            onFinishFailed={this.saveUserFailed}
            ref={this.formRef}
          >
            <div className="kuika-transfer-ownership__field kuika-flex-column--gap-xs">
              <KuikaText className="kuika-transfer-ownership__label">Search member</KuikaText>
              <Form.Item name="userName" rules={[{ required: true, message: "Please input a user name!" }]}>
                <KuikaSelectbox
                  data-testid="transferOwnershipMemberSelect"
                  placeholder="Write email address or full name..."
                  searchable
                  options={this.getMemberOptions()}
                />
              </Form.Item>
            </div>
            <div className="kuika-transfer-ownership__field kuika-flex-column--gap-xs">
              <KuikaText className="kuika-transfer-ownership__label">Select a new role for the current owner</KuikaText>
              <Form.Item name="oldOwnerNewProjectRole" rules={[{ required: true, message: "Please input a role!" }]}>
                <KuikaSelectbox
                  data-testid="transferOwnershipOldOwnerRoleSelect"
                  placeholder="Select role"
                  options={this.getRoleOptions()}
                />
              </Form.Item>
            </div>
          </Form>
        </div>
      </KModal>
    );
  }
}

const transferProjectOwnershipModal = withContext<IProps>(
  withHistory<IProps & IWithHistoryProp & IWithContextProp>(TransferProjectOwnershipModal)
);
export { transferProjectOwnershipModal as TransferProjectOwnershipModal };
